import { Injectable, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SearchState } from '@cia-front-end-apps/shared/api-interaction';
import { injectQuery, injectQueryClient, queryOptions } from '@ngneat/query';
import { map } from 'rxjs/operators';
import { routeParts } from '@hq-core/routes';
import { NavBadgeService } from '@hq-core/navigation/nav-badge.service';
import { BrandResource } from './resources/brand.resource';

@Injectable({
    providedIn: 'root',
})
export class BrandAssignmentService {
    private readonly query = injectQuery();
    private readonly queryClient = injectQueryClient();
    private readonly brandResource = inject(BrandResource);
    private readonly navBadgeService = inject(NavBadgeService);

    // Polling count query used for the nav badge — kept alive for the service lifetime.
    private readonly countQuery = this.getBrandQueueCountQuery();
    // Capture .result in the field initializer (injection context) so the computed below
    // can read it lazily without requiring an injection context at call time.
    private readonly countQueryResult = this.countQuery.result;

    /** Shared count signal — consume this in components instead of calling getBrandQueueCountQuery() directly. */
    readonly queueCount = computed(() => {
        const r = this.countQueryResult();
        return r.isSuccess ? (r.data.meta?.itemCount ?? r.data.items?.length ?? 0) : 0;
    });

    constructor() {
        // Publish the total queue size to NavBadgeService whenever the count query updates.
        this.countQuery.result$.pipe(
            map(result => result.isSuccess
                ? (result.data.meta?.itemCount ?? result.data.items?.length)
                : undefined
            ),
            takeUntilDestroyed(),
        ).subscribe(count => {
            this.navBadgeService.setBadge(`/${routeParts.brandAssignment}`, count);
        });
    }

    getBrandQueueOptions(searchState: SearchState) {
        return queryOptions({
            queryKey: [
                'brandQueue',
                searchState.pageIndex,
                searchState.pageSize,
                searchState.search,
                searchState.sortBy,
                searchState.sortDirection,
            ],
            queryFn: () => this.brandResource.getAll(searchState),
            staleTime: 0,
        });
    }

    getBrandQueue(searchState: SearchState) {
        return this.query(this.getBrandQueueOptions(searchState));
    }

    /**
     * A lightweight count-only query (no filter, page size 1) used for the nav badge
     * and the review-queue subtitle. Polls periodically so the badge stays fresh.
     */
    getBrandQueueCountQuery() {
        return this.query({
            queryKey: ['brandQueueCount'],
            queryFn: () => this.brandResource.getAll(new SearchState({ pageIndex: 0, pageSize: 1 })),
            staleTime: 0,
            refetchInterval: 60000,
            refetchOnWindowFocus: 'always',
        });
    }

    /** Resets all brand-queue and count caches to pending — call after demo scenario changes. */
    resetBrandQueues(): void {
        this.queryClient.resetQueries({ queryKey: ['brandQueue'] });
        this.queryClient.resetQueries({ queryKey: ['brandQueueCount'] });
    }
}
